import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Flame } from "lucide-react";

/**
 * Premium gamified Quick Action card.
 *
 * Variants:
 *  - "hero":     full-width dominant action with progress ring, headline stat,
 *                streak chip and reward preview.
 *  - "standard": compact tile with icon, headline stat, motivational chip and
 *                an optional progress bar.
 *
 * Each instance is colour-themed via `accent` (hex) and has unique ambient
 * motion (mesh drift + breathing icon + shimmer sweep).
 */
export default function QuickActionCard({
  to,
  icon: Icon,
  title,
  tagline,           // e.g. "Today's goal" / "Right now"
  primaryValue,      // big number stat (e.g. "4,287")
  primaryUnit,       // small unit after big number (e.g. "steps")
  secondary,         // secondary line (e.g. "62 cal · 7-day streak")
  reward,            // small chip text e.g. "+50 pts available"
  rewardIcon: RewardIcon,
  progress,          // 0..1 for bar / ring
  showRing = false,
  accent = "#4ade80",
  variant = "standard",  // "hero" | "standard"
  ctaLabel = "Start",
  testId,
}) {
  const isHero = variant === "hero";
  return (
    <Link
      to={to}
      data-testid={testId}
      className={`ps-glass-card relative overflow-hidden group block ${isHero ? "p-5" : "p-4"} hover:border-[${accent}80] focus:outline-none`}
      style={{
        boxShadow: `0 1px 0 0 rgba(255,255,255,0.08) inset, 0 -1px 0 0 rgba(0,0,0,0.25) inset, 0 28px 60px -28px ${accent}40`,
      }}
    >
      {/* Ambient gradient mesh */}
      <div
        aria-hidden
        className="ps-mesh-drift absolute inset-[-30%] opacity-50 pointer-events-none"
        style={{
          background: `radial-gradient(circle at 30% 20%, ${accent}40 0%, transparent 40%), radial-gradient(circle at 80% 70%, ${accent}25 0%, transparent 45%)`,
          filter: "blur(20px)",
        }}
      />
      {/* Gradient border ring */}
      <div
        aria-hidden
        className="absolute inset-0 rounded-3xl pointer-events-none opacity-50"
        style={{
          background: `linear-gradient(135deg, ${accent}66 0%, transparent 40%, transparent 60%, ${accent}33 100%)`,
          padding: 1,
          WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
          WebkitMaskComposite: "xor",
          maskComposite: "exclude",
        }}
      />
      {/* Diagonal shimmer sweep */}
      <div
        aria-hidden
        className="absolute inset-0 overflow-hidden pointer-events-none rounded-3xl"
      >
        <div
          className="ps-shimmer-x absolute -inset-y-4 w-1/3"
          style={{
            background: `linear-gradient(100deg, transparent 0%, ${accent}30 50%, transparent 100%)`,
            transform: "translateX(-120%)",
          }}
        />
      </div>
      {/* Particles */}
      {isHero && (
        <>
          <span aria-hidden className="ps-particle absolute bottom-3 left-8 w-1.5 h-1.5 rounded-full" style={{ background: accent }} />
          <span aria-hidden className="ps-particle absolute bottom-2 left-1/3 w-1 h-1 rounded-full" style={{ background: accent, animationDelay: "1.4s" }} />
          <span aria-hidden className="ps-particle absolute bottom-4 left-2/3 w-1.5 h-1.5 rounded-full" style={{ background: accent, animationDelay: "2.6s" }} />
        </>
      )}

      <div className="relative flex items-start gap-4">
        {/* Embossed glowing icon button — physical 3D look */}
        <div
          className={`shrink-0 rounded-2xl flex items-center justify-center relative transition-transform duration-200 group-hover:scale-105 group-active:scale-95 ${isHero ? "w-20 h-20" : "w-16 h-16"}`}
          style={{
            background: `linear-gradient(160deg, ${accent}55 0%, ${accent}22 40%, ${accent}10 100%)`,
            border: `1px solid ${accent}66`,
            boxShadow:
              // upper highlight
              `inset 0 1.5px 0 rgba(255,255,255,0.35),` +
              // lower inner shadow for depth
              `inset 0 -3px 6px rgba(0,0,0,0.45),` +
              // tight ambient ring
              `inset 0 0 18px ${accent}26,` +
              // outer glow + drop
              `0 8px 22px -8px ${accent}88,` +
              `0 2px 0 rgba(0,0,0,0.4)`,
          }}
        >
          {/* Top-light reflection */}
          <span
            aria-hidden
            className="absolute inset-x-2 top-1 h-1/3 rounded-t-xl pointer-events-none"
            style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.22), transparent)" }}
          />
          {/* Bottom-shadow ridge */}
          <span
            aria-hidden
            className="absolute inset-x-2 bottom-1 h-[2px] rounded-full pointer-events-none"
            style={{ background: `linear-gradient(90deg, transparent, ${accent}88, transparent)`, filter: "blur(2px)" }}
          />
          <Icon
            size={isHero ? 38 : 32}
            color={accent}
            className="ps-breathe relative"
            strokeWidth={2.4}
            style={{ filter: `drop-shadow(0 2px 4px ${accent}55) drop-shadow(0 0 12px ${accent}66)` }}
          />
        </div>

        <div className="flex-1 min-w-0">
          {tagline && (
            <div
              className="text-[9px] uppercase tracking-[0.25em] font-bold"
              style={{ color: accent }}
            >
              {tagline}
            </div>
          )}
          <div className={`ps-heading font-extrabold leading-tight ${isHero ? "text-xl" : "text-base"}`}>
            {title}
          </div>
          {primaryValue !== undefined && primaryValue !== null && (
            <div className={`mt-1 flex items-baseline gap-1 ${isHero ? "text-3xl" : "text-xl"} ps-heading font-extrabold ps-tabular`} data-testid={`${testId}-stat`}>
              {primaryValue}
              {primaryUnit && (
                <span className="text-[10px] ps-serif ps-italic font-normal text-slate-400 uppercase tracking-widest">
                  {primaryUnit}
                </span>
              )}
            </div>
          )}
          {secondary && (
            <div className="text-[11px] text-slate-400 mt-1 ps-serif ps-italic">
              {secondary}
            </div>
          )}
        </div>

        {showRing && progress !== undefined && (
          <ProgressRing pct={progress} size={isHero ? 64 : 48} stroke={6} accent={accent} />
        )}
      </div>

      {/* Progress bar (only when not using ring) */}
      {!showRing && progress !== undefined && (
        <div className="relative mt-4 h-1.5 rounded-full bg-[#0E1F2A] overflow-hidden">
          <div
            className="h-full rounded-full"
            style={{
              width: `${Math.min(100, progress * 100)}%`,
              background: `linear-gradient(90deg, ${accent}, ${accent}cc)`,
              transition: "width 0.8s cubic-bezier(.2,.7,.2,1)",
              boxShadow: `0 0 12px ${accent}88`,
            }}
          />
        </div>
      )}

      {/* Reward + CTA row */}
      <div className="relative mt-4 flex items-center justify-between">
        {reward && (
          <div
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide"
            style={{
              background: `${accent}1f`,
              color: accent,
              border: `1px solid ${accent}44`,
            }}
          >
            {RewardIcon && <RewardIcon size={11} />}
            {reward}
          </div>
        )}
        <div
          className={`ml-auto inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-widest ${isHero ? "px-3 py-1.5 rounded-full" : ""}`}
          style={isHero ? { background: accent, color: "#022014", boxShadow: `0 6px 18px ${accent}55` } : { color: accent }}
        >
          {ctaLabel}
          <ChevronRight size={12} />
        </div>
      </div>
    </Link>
  );
}

function ProgressRing({ pct = 0, size = 56, stroke = 5, accent = "#4ade80" }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - Math.min(1, pct) * c;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#15303E" strokeWidth={stroke} fill="none" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke={accent}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          fill="none"
          style={{
            transition: "stroke-dashoffset 1.2s cubic-bezier(.2,.7,.2,1)",
            filter: `drop-shadow(0 0 6px ${accent}88)`,
          }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center text-[11px] font-extrabold ps-tabular" style={{ color: accent }}>
        {Math.round(Math.min(1, pct) * 100)}%
      </div>
    </div>
  );
}

/* Optional named export for the streak fire chip used inside cards. */
export function StreakChip({ days = 0, accent = "#FBBF24" }) {
  if (!days) return null;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide"
      style={{
        background: `${accent}1f`,
        color: accent,
        border: `1px solid ${accent}44`,
      }}
    >
      <Flame size={10} /> {days}-day streak
    </span>
  );
}
